import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { UserPlus } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';

interface AddFamilyMemberFormProps {
  onAdd: (email: string) => Promise<void>;
  isAdding: boolean;
  currentCount: number;
  maxMembers: number;
}

const AddFamilyMemberForm: React.FC<AddFamilyMemberFormProps> = ({
  onAdd,
  isAdding,
  currentCount,
  maxMembers
}) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const canAddMore = currentCount < maxMembers;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    if (!email.toLowerCase().endsWith('@gmail.com')) {
      setError('Only Gmail addresses are supported');
      return;
    }

    try {
      await onAdd(email.trim());
      setEmail('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to add family member');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add Family Member</CardTitle>
      </CardHeader>
      <CardContent>
        {!canAddMore ? (
          <Alert>
            <AlertDescription>
              You have reached the maximum number of family members ({maxMembers}).
            </AlertDescription>
          </Alert>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Gmail Address
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="member@gmail.com"
                disabled={isAdding}
              />
              <p className="text-sm text-muted-foreground mt-1">
                Enter the Gmail address of the person you want to add. They will be added immediately if
                they have an account, or invited to join when they sign up.
              </p>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button type="submit" disabled={isAdding || !canAddMore}>
              <UserPlus className="w-4 h-4 mr-2" />
              {isAdding ? 'Adding...' : 'Add Member'}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
};

export default AddFamilyMemberForm;
